import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { Box, Tooltip } from "@mui/material";
import CanvasStackPicker from "./CanvasStackPicker";
import { DESCRIBING_TYPES } from "../utils/graphCompleteness";

// image px — how close the cursor must be to an existing pipe to show a junction
// hotspot. Kept tight (below the server's own SPLIT_TOLERANCE of 5px in
// connectionController.js) so it only fires when the cursor is genuinely on
// top of a line — in a congested drawing with many close-together pipes, a
// looser radius kept stealing clicks meant for a nearby but unrelated line.
const HOTSPOT_TOLERANCE = 4; // image px
// ...but never less than this many screen px, or a pipe is near-impossible to hit zoomed out.
const HOTSPOT_TOLERANCE_SCREEN_PX = 8;

// Shortest distance from `point` ({x,y}) to the segment [segStart, segEnd] ([x,y] arrays),
// plus the closest point on the segment — used for line-hotspot hit-testing below.
function distPointToSegment(point, segStart, segEnd) {
  const [ax, ay] = segStart;
  const [bx, by] = segEnd;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return { distance: Math.hypot(point.x - ax, point.y - ay), point: { x: ax, y: ay } };
  }
  let t = ((point.x - ax) * dx + (point.y - ay) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const px = ax + t * dx;
  const py = ay + t * dy;
  return { distance: Math.hypot(point.x - px, point.y - py), point: { x: px, y: py } };
}

// Finds the closest point, across all connections' path segments, to `mousePos` — used to
// offer a "split this pipe into a junction" hotspot, whether the user is choosing where a
// new connection starts, or where it ends/reroutes to.
// A connection with no stored route (e.g. one from "Trace lines") is drawn as a
// straight line between its ends' centres (Connection.js), so that line is its
// one segment here; splitting it on the server builds the same route.
const centreOf = (el) => el?.boundingBox && [el.boundingBox.x + el.boundingBox.width / 2, el.boundingBox.y + el.boundingBox.height / 2];
const idOf = (v) => String((typeof v === "object" && v?._id) ? v._id : v);

function connectionSegments(connection, elementsById) {
  const stored = connection.metadata?.path_data?.path;
  if (Array.isArray(stored) && stored.length > 0) return stored;
  if (!elementsById || connection.targetType === "Connection") return null;
  const start = centreOf(elementsById.get(idOf(connection.source)));
  const end = centreOf(elementsById.get(idOf(connection.target)));
  return start && end ? [{ start, end }] : null;
}

export function findNearestConnectionHotspot(mousePos, connections, excludeConnectionId, tolerance, elementsById) {
  if (!mousePos) return null;
  let best = null;
  for (const connection of connections) {
    if (excludeConnectionId && String(connection._id) === String(excludeConnectionId)) continue;
    const segments = connectionSegments(connection, elementsById);
    if (!segments) continue;
    for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
      const seg = segments[segmentIndex];
      const { distance, point } = distPointToSegment(mousePos, seg.start, seg.end);
      if (distance <= tolerance && (!best || distance < best.distance)) {
        best = { connection, segmentIndex, point };
      }
    }
  }
  return best;
}

// One clickable element dot. A plain div with inline style (no per-dot style
// class), memoised: on a large drawing there are thousands, and only the one
// or two whose colour changes (hover, source) should re-render.
const ElementDot = memo(function ElementDot({ el, scale, fill, onEnter, onLeave, onPick }) {
  const cx = (el.boundingBox.x + el.boundingBox.width / 2) * scale;
  const cy = (el.boundingBox.y + el.boundingBox.height / 2) * scale;
  return (
    <div
      data-hit-type="conn-dot"
      data-hit-id={el._id}
      style={{
        position: "absolute", left: cx - 16, top: cy - 16, width: 32, height: 32,
        borderRadius: "50%", background: fill, border: "2px solid #1976d2",
        opacity: 0.7, cursor: "pointer", zIndex: 20, boxSizing: "border-box",
      }}
      onMouseEnter={() => onEnter(el)}
      onMouseLeave={onLeave}
      onClick={(e) => { e.stopPropagation(); onPick(el); }}
    />
  );
});

const ConnectionCreationOverlay = ({
  elements,
  connections = [],
  scale,
  containerRef,
  onComplete,
  onCancel,
  isActive,
  rerouteAnchor,   // { point: {x,y}, connectionId, anchoredEnd, handlePoint?: {x,y} } — set when rerouting an existing connection
  onRerouteComplete, // (rerouteAnchor, targetElement, pathPoints) => void
  onSplitConnection, // (connection, segmentIndex, point) => Promise<{junction} | null>
  onDescribeConnection, // (textLabel, connection) => void — text started on a pipe: link it, don't split
  visibleRect,          // image-coord area currently drawn ({x0,y0,x1,y1}) — dots outside it are skipped; null = all
}) => {
  const elementsById = useMemo(() => new Map((elements || []).map(el => [String(el._id), el])), [elements]);
  const hotspotTolerance = Math.max(HOTSPOT_TOLERANCE, HOTSPOT_TOLERANCE_SCREEN_PX / (scale || 1));
  const [step, setStep] = useState("chooseSource");
  const [source, setSource] = useState(null);
  const [path, setPath] = useState([]);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [mousePos, setMousePos] = useState(null);   // image coordinates
  const [rawMouse, setRawMouse] = useState(null);   // viewport coordinates for tooltip
  // stackPicker — disambiguation menu for clicks that land on more than one
  // hotspot at once (e.g. an instrument and its text label sharing a
  // bounding-box center). Local to this component, independent of
  // DrawingView.js's own CanvasStackPicker usage for normal selection.
  const [stackPicker, setStackPicker] = useState(null); // {hits, anchorPosition} | null
  const suppressNextClickRef = useRef(false);

  // scaleRef — keeps the native mousemove handler current without re-binding on zoom
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  // Reset state when overlay is (re)activated
  useEffect(() => {
    if (isActive) {
      if (rerouteAnchor) {
        // Reroute mode: skip source selection, seed path from anchor then immediately
        // add the clicked handle position so drawing starts there (not at the far anchor).
        setStep("drawing");
        setSource(null);
        setPath(rerouteAnchor.handlePoint
          ? [rerouteAnchor.point, rerouteAnchor.handlePoint]
          : [rerouteAnchor.point]);
      } else {
        setStep("chooseSource");
        setSource(null);
        setPath([]);
      }
      setHoveredElement(null);
      setMousePos(null);
      setRawMouse(null);
    }
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach mousemove to the scroll container so it fires over the full scrollable
  // area, not just the viewport-sized overlay Box.
  useEffect(() => {
    if (!isActive || !containerRef.current) return;
    const container = containerRef.current;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      setMousePos({
        x: (e.clientX - rect.left + container.scrollLeft) / scaleRef.current,
        y: (e.clientY - rect.top  + container.scrollTop)  / scaleRef.current,
      });
      setRawMouse({ x: e.clientX, y: e.clientY });
    };

    container.addEventListener("mousemove", onMove);
    return () => container.removeEventListener("mousemove", onMove);
  }, [isActive, containerRef]); // scale accessed via ref — no need to re-bind on zoom

  // Escape to cancel
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, onCancel]);

  const handleElementClick = (element) => {
    if (step === "chooseSource") {
      setSource(element);
      setPath([{
        x: element.boundingBox.x + element.boundingBox.width  / 2,
        y: element.boundingBox.y + element.boundingBox.height / 2,
      }]);
      setStep("drawing");
    } else if (step === "drawing" && element) {
      // In reroute mode any element is a valid target; in create mode exclude the source.
      // (A freshly-created junction from a line hotspot can never equal the source, so this
      // guard is a no-op in that case.)
      if (!rerouteAnchor && element._id === source?._id) return;
      const last = path[path.length - 1];
      const targetCenter = {
        x: element.boundingBox.x + element.boundingBox.width  / 2,
        y: element.boundingBox.y + element.boundingBox.height / 2,
      };
      const dx = Math.abs(targetCenter.x - last.x);
      const dy = Math.abs(targetCenter.y - last.y);
      const finalPath = (dx < 1 || dy < 1)
        ? [...path, targetCenter]
        : dx > dy
          ? [...path, { x: targetCenter.x, y: last.y }, targetCenter]
          : [...path, { x: last.x, y: targetCenter.y }, targetCenter];

      if (rerouteAnchor) {
        onRerouteComplete(rerouteAnchor, element, finalPath);
      } else {
        onComplete(source, element, finalPath);
      }
    }
  };

  // Closest point on any existing connection's pipe to the cursor, offered as a
  // "click here to insert a junction" hotspot. Available both when choosing a source
  // (start a new connection from a junction inserted into an existing pipe) and while
  // drawing/rerouting (end/extend onto a junction) — handleElementClick already branches
  // correctly on `step` for any element, real or synthetic. Element-dot hover always wins
  // when both are in range — only compute this when no element is currently hovered.
  const hotspot = useMemo(() => {
    if (!isActive || hoveredElement || !mousePos) return null;
    return findNearestConnectionHotspot(mousePos, connections, rerouteAnchor?.connectionId, hotspotTolerance, elementsById);
  }, [isActive, hoveredElement, mousePos, connections, rerouteAnchor, elementsById, hotspotTolerance]);

  // Accepts an explicit override so the stack picker (below) can pass the
  // exact hotspot it offered — by the time onSplitConnection's await
  // resolves, the reactive `hotspot` memo may have already gone stale/null
  // (e.g. the mouse left the canvas to click the picker menu).
  // Text names or describes things, it doesn't flow — so a connection started on
  // a text label that ends on a pipe becomes a 'describes' link to that pipe,
  // and the pipe is left whole instead of being split into a junction.
  const linksTextToPipe = step === "drawing" && !rerouteAnchor && DESCRIBING_TYPES.has(source?.elementType) && !!onDescribeConnection;

  const handleHotspotClick = async (hotspotOverride) => {
    const hs = hotspotOverride || hotspot;
    if (!hs) return;
    if (linksTextToPipe) {
      onDescribeConnection(source, hs.connection);
      return;
    }
    if (!onSplitConnection) return;
    const result = await onSplitConnection(hs.connection, hs.segmentIndex, hs.point);
    if (!result) return; // failure already surfaced by the caller
    handleElementClick(result.junction);
  };

  // Disambiguates a click that lands on more than one hotspot at once.
  // Element dots are real DOM nodes (tagged data-hit-type="conn-dot" below),
  // so document.elementsFromPoint reliably finds every one stacked at that
  // pixel — mirroring the same technique DrawingView.js's own stack picker
  // uses for normal selection, just scoped to this overlay's own hotspots.
  // A pipe running through the same point is checked independently of
  // hoveredElement (unlike the hover-preview `hotspot` memo above), since a
  // genuine junction candidate shouldn't be suppressed just because an
  // element dot's hover happens to be winning the preview at that instant.
  const handleAmbiguityMouseDownCapture = (e) => {
    if (stackPicker) return;
    const container = containerRef.current;
    if (!container) return;

    const seen = new Set();
    const hits = [];
    for (const node of document.elementsFromPoint(e.clientX, e.clientY)) {
      const id = node.dataset?.hitId;
      if (node.dataset?.hitType !== "conn-dot" || !id || seen.has(id)) continue;
      seen.add(id);
      const item = elements.find((el) => el._id === id);
      if (item) hits.push({ type: "element", item });
    }

    const rect = container.getBoundingClientRect();
    const clickImagePos = {
      x: (e.clientX - rect.left + container.scrollLeft) / scaleRef.current,
      y: (e.clientY - rect.top  + container.scrollTop)  / scaleRef.current,
    };
    const hotspotResult = findNearestConnectionHotspot(clickImagePos, connections, rerouteAnchor?.connectionId, hotspotTolerance, elementsById);
    if (hotspotResult) hits.push({ type: "junction", item: hotspotResult.connection, hotspotData: hotspotResult });

    if (hits.length <= 1) return; // unambiguous — let the native click path proceed

    e.stopPropagation();
    suppressNextClickRef.current = true;
    // Self-clearing safety net: opening the picker mounts a full-screen MUI
    // modal backdrop synchronously, so mousedown and the following mouseup
    // can end up targeting different elements (dot vs. backdrop) — when that
    // happens the browser skips dispatching the follow-up `click` entirely,
    // so handleAmbiguityClickCapture never gets a chance to reset the flag.
    // Without this, the stale `true` silently swallows the *next* unrelated
    // click — e.g. the click that's supposed to pick a menu item.
    setTimeout(() => { suppressNextClickRef.current = false; }, 0);
    setStackPicker({ hits, anchorPosition: { left: e.clientX, top: e.clientY } });
  };

  const handleAmbiguityClickCapture = (e) => {
    if (suppressNextClickRef.current) {
      e.stopPropagation();
      suppressNextClickRef.current = false;
    }
  };

  // Stable handlers for the dots (the click handler depends on step/path/source,
  // so it's reached through a ref), and the dot list itself only rebuilt when
  // what it shows changes — not on every mouse move.
  const handleElementClickRef = useRef(handleElementClick);
  handleElementClickRef.current = handleElementClick;
  const onDotPick = useCallback((el) => handleElementClickRef.current(el), []);
  const onDotLeave = useCallback(() => setHoveredElement(null), []);
  const sourceId = source?._id;
  const hoveredId = hoveredElement?._id;
  const dots = useMemo(() => (isActive ? elements.filter((el) => {
    if (!visibleRect || el._id === sourceId) return true;
    const b = el.boundingBox;
    return !(b.x > visibleRect.x1 || b.x + b.width < visibleRect.x0 || b.y > visibleRect.y1 || b.y + b.height < visibleRect.y0);
  }).map((el) => (
    <ElementDot
      key={el._id}
      el={el}
      scale={scale}
      fill={step === "chooseSource" ? "#1976d2" : el._id === sourceId ? "#1976d2" : hoveredId === el._id ? "#90caf9" : "#fff"}
      onEnter={setHoveredElement}
      onLeave={onDotLeave}
      onPick={onDotPick}
    />
  )) : null), [isActive, elements, scale, step, sourceId, hoveredId, onDotLeave, onDotPick, visibleRect]);

  const handleStackPick = (hit) => {
    setStackPicker(null);
    if (hit.type === "element") handleElementClick(hit.item);
    else if (hit.type === "junction") handleHotspotClick(hit.hotspotData);
  };

  if (!isActive) return null;

  const last = path.length > 0 ? path[path.length - 1] : null;
  const previewDx = mousePos && last ? Math.abs(mousePos.x - last.x) : 0;
  const previewDy = mousePos && last ? Math.abs(mousePos.y - last.y) : 0;
  const previewHoriz = previewDx >= previewDy;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0, left: 0, width: "100%", height: "100%",
        pointerEvents: "auto",
        zIndex: 10,
        cursor: step === "drawing" ? "crosshair" : "default",
      }}
      onMouseDownCapture={handleAmbiguityMouseDownCapture}
      onClickCapture={handleAmbiguityClickCapture}
      onClick={(e) => {
        // Add a waypoint on background clicks; element dots call stopPropagation.
        // Use state directly (not refs) — React recreates this closure on every render
        // so it always captures the latest values without timing issues.
        if (step !== "drawing" || !mousePos || !last) return;
        const dx = Math.abs(mousePos.x - last.x);
        const dy = Math.abs(mousePos.y - last.y);
        setPath(prev => [...prev, dx > dy ? { x: mousePos.x, y: last.y } : { x: last.x, y: mousePos.y }]);
      }}
    >
      {/* Tooltip anchored at raw viewport coordinates so it stays under the cursor.
          width/height must be strings — MUI treats numeric 0–1 as percentages. */}
      {rawMouse && (
        <Tooltip
          open
          title={
            hoveredElement && (step === "chooseSource" || rerouteAnchor || hoveredElement._id !== source?._id)
              ? (step === "chooseSource" ? "Choose source element" : "Select target element")
              : hotspot
                ? (step === "chooseSource" ? "Click to start from a new pipe junction" : linksTextToPipe ? "Click to link to this pipe (describes)" : "Click to insert a pipe junction")
                : step === "chooseSource"
                  ? "Choose source element"
                  : "Click to draw pipe route"
          }
          placement="top"
          arrow
        >
          <Box
            sx={{
              position: "fixed",
              left: `${rawMouse.x}px`,
              top: `${rawMouse.y - 32}px`,
              width: "1px",
              height: "1px",
              pointerEvents: "none",
              zIndex: 9999,
            }}
          />
        </Tooltip>
      )}

      {/* SVG preview — overflow:visible so it renders across the full scroll area */}
      <svg
        width="100%"
        height="100%"
        style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}
      >
        {path.length > 1 && (
          <polyline
            points={path.map(p => `${p.x * scale},${p.y * scale}`).join(" ")}
            fill="none"
            stroke="#1976d2"
            strokeWidth={3}
          />
        )}
        {step === "drawing" && last && mousePos && (
          <line
            x1={last.x * scale}
            y1={last.y * scale}
            x2={previewHoriz ? mousePos.x * scale : last.x * scale}
            y2={previewHoriz ? last.y * scale     : mousePos.y * scale}
            stroke="#1976d2"
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}
      </svg>

      {/* Line hotspot — click to split the underlying pipe and insert a junction here.
          Diamond shape (vs. circular element dots) keeps the two affordances visually distinct.
          When drawing from a text label it links instead of splitting, shown as a round sky dot. */}
      {hotspot && (
        <Box
          sx={{
            position: "absolute",
            left: hotspot.point.x * scale - 8,
            top: hotspot.point.y * scale - 8,
            width: 16,
            height: 16,
            transform: linksTextToPipe ? "none" : "rotate(45deg)",
            borderRadius: linksTextToPipe ? "50%" : 0,
            background: linksTextToPipe ? "#38bdf8" : "#ff9800",
            border: "2px solid #fff",
            boxShadow: linksTextToPipe ? "0 0 6px rgba(56,189,248,0.6)" : "0 0 6px rgba(255,152,0,0.6)",
            cursor: "pointer",
            zIndex: 15,
          }}
          onClick={(e) => { e.stopPropagation(); handleHotspotClick(); }}
        />
      )}

      {/* Clickable element dots — pointerEvents:auto overrides the parent none */}
      {dots}

      <CanvasStackPicker
        stackPicker={stackPicker}
        onPick={handleStackPick}
        onClose={() => setStackPicker(null)}
      />
    </Box>
  );
};

export default ConnectionCreationOverlay;
